// src/components/profile-input/steps/PersonalDetailsStep.jsx
import React, { useState, useEffect } from "react";
import { Grid, Typography, Divider, MenuItem, Select, InputAdornment, TextField } from "@mui/material";
import { TextInput } from "../FormFields";
import { 
  options, 
  calculateAge
} from "../formUtils";
import { countryCodes, getDefaultCountryCode } from "../../../../components/data/countryCodes";

const PersonalDetailsStep = ({ formData, handleInputChange, errors = {} }) => {
  const [dateError, setDateError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [heightUnit, setHeightUnit] = useState("feet");
  const [heightDisplayValue, setHeightDisplayValue] = useState("");
  const [languagesText, setLanguagesText] = useState("");
  const [bodyTypeInput, setBodyTypeInput] = useState("");
  const [showBodyTypeOptions, setShowBodyTypeOptions] = useState(false);
  const [complexionInput, setComplexionInput] = useState("");
  const [showComplexionOptions, setShowComplexionOptions] = useState(false);
  const [residentialInput, setResidentialInput] = useState("");
  const [showResidentialOptions, setShowResidentialOptions] = useState(false);
  
  // Mobile number states
  const [mobileDisplay, setMobileDisplay] = useState('');
  const [mobileCountryCode, setMobileCountryCode] = useState(getDefaultCountryCode());
  const [isMobileCodeEditing, setIsMobileCodeEditing] = useState(false);
  const [mobileCodeInput, setMobileCodeInput] = useState(getDefaultCountryCode());

  // Initialize from form data
  useEffect(() => {
    const heightData = formData.physical_details?.height;
    
    // Handle height initialization
    if (heightData) {
      if (typeof heightData === 'object' && heightData.value) {
        // New structure with value and unit
        setHeightDisplayValue(heightData.value);
        setHeightUnit(heightData.unit || "feet");
      } else if (typeof heightData === 'string') {
        // Legacy support - try to detect if it's cm or feet
        if (heightData.includes("'") || heightData.includes('"')) {
          setHeightDisplayValue(heightData);
          setHeightUnit("feet");
        } else if (!isNaN(parseInt(heightData))) {
          // Assume it's cm if it's just a number
          setHeightDisplayValue(heightData);
          setHeightUnit("cm");
        } else {
          setHeightDisplayValue("");
          setHeightUnit("feet");
        }
      } else {
        setHeightDisplayValue("");
        setHeightUnit("feet");
      }
    } else {
      setHeightDisplayValue("");
      setHeightUnit("feet");
    }

    // Initialize languages text from array
    const languagesData = formData.personal_details?.languages_known || [];
    if (Array.isArray(languagesData) && languagesData.length > 0) {
      setLanguagesText(languagesData.join(", "));
    } else {
      setLanguagesText("");
    }

    // Initialize body type input
    setBodyTypeInput(formData.physical_details?.body_type || "");

    // Initialize complexion input
    setComplexionInput(formData.physical_details?.complexion || "");

    // Initialize residential input
    setResidentialInput(formData.residence?.residential_status || "");

    // Initialize mobile number
    if (formData.personal_details?.mobile_country_code) {
      setMobileCountryCode(formData.personal_details.mobile_country_code);
      setMobileCodeInput(formData.personal_details.mobile_country_code);
    }
    
    if (formData.personal_details?.mobile_number) {
      setMobileDisplay(formData.personal_details.mobile_number);
    }
  }, [
    formData.physical_details?.height,
    formData.personal_details?.languages_known,
    formData.physical_details?.body_type,
    formData.physical_details?.complexion,
    formData.residence?.residential_status,
    formData.personal_details?.mobile_number,
    formData.personal_details?.mobile_country_code
  ]);

  // Validate date function
  const isValidDate = (day, month, year) => {
    // Check if any part is zero
    if (day === 0 || month === 0 || year === 0) {
      return false;
    }

    // Check month range
    if (month < 1 || month > 12) {
      return false;
    }

    // Check day range based on month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return false;
    }

    // Check year range (reasonable age limits)
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
      return false;
    }

    // Additional check using Date object to catch any edge cases
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || 
        date.getMonth() !== month - 1 || 
        date.getDate() !== day) {
      return false;
    }

    return true;
  };

  // Handle date input with validation
  const handleDateChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      handleInputChange("personal_details.date_of_birth", value);
      setDateError("");
      return;
    }

    let formattedValue = value.replace(/\D/g, '');

    if (formattedValue.length > 8) {
      formattedValue = formattedValue.substring(0, 8);
    }

    if (formattedValue.length >= 3) {
      formattedValue = formattedValue.substring(0, 2) + '-' + formattedValue.substring(2);
    }
    if (formattedValue.length >= 6) {
      formattedValue = formattedValue.substring(0, 5) + '-' + formattedValue.substring(5);
    }

    handleInputChange("personal_details.date_of_birth", formattedValue);

    // Validate complete date
    if (formattedValue.length === 10) {
      const datePattern = /^(\d{2})-(\d{2})-(\d{4})$/;
      const match = formattedValue.match(datePattern);
      
      if (!match) {
        setDateError("Invalid date format. Use DD-MM-YYYY");
        return;
      }

      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      // Check for all zeros
      if (day === 0 && month === 0 && year === 0) {
        setDateError("Date cannot be all zeros");
        return;
      }

      // Validate date components
      if (!isValidDate(day, month, year)) {
        setDateError("Invalid date. Please enter a valid date");
        return;
      }

      // Calculate age and check if person is at least 18 years old
      const birthDate = new Date(year, month - 1, day);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 18) {
        setDateError("You must be at least 18 years old");
        return;
      }

      if (age > 100) {
        setDateError("Age cannot be more than 100 years");
        return;
      }

      // If all validations pass
      setDateError("");
      calculateAge(formattedValue);
      
    } else {
      setDateError("");
    }
  };

  // Mobile number handlers
  const handleMobileCountryCodeSelect = (e) => {
    const newCode = e.target.value;
    setMobileCountryCode(newCode);
    setMobileCodeInput(newCode);
    setIsMobileCodeEditing(false);
    handleInputChange("personal_details.mobile_country_code", newCode);
  };

  const handleMobileCodeInputChange = (e) => {
    const value = e.target.value;
    // Allow + and digits only
    const formattedValue = value.replace(/[^\d+]/g, '');
    setMobileCodeInput(formattedValue);
  };

  const handleMobileCodeBlur = () => {
    setIsMobileCodeEditing(false);
    // Check if the typed code exists in the list
    const matchedCountry = countryCodes.find(c => c.value === mobileCodeInput);
    if (matchedCountry) {
      setMobileCountryCode(matchedCountry.value);
    } else {
      // Keep the typed value if it doesn't match
      setMobileCountryCode(mobileCodeInput);
    }
    handleInputChange("personal_details.mobile_country_code", mobileCodeInput);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    // Allow only digits
    const digitsOnly = value.replace(/\D/g, '');
    setMobileDisplay(digitsOnly);
    handleInputChange("personal_details.mobile_number", digitsOnly);
  };

  // ========== HEIGHT HANDLERS (Exactly like PreferencesStep) ==========

  // Handle height unit change
  const handleHeightUnitChange = (e) => {
    const newUnit = e.target.value;
    setHeightUnit(newUnit);
    setHeightDisplayValue("");
    setHeightError("");
    handleInputChange("physical_details.height", {
      value: "",
      unit: newUnit
    });
  };

  // Handle height input
  const handleHeightChange = (e) => {
    const value = e.target.value;
    setHeightDisplayValue(value);
    setHeightError("");

    // Update form data immediately
    handleInputChange("physical_details.height", {
      value: value,
      unit: heightUnit
    });
  };

  // Validate height on blur
  const validateHeight = () => {
    const value = heightDisplayValue;

    if (!value) {
      handleInputChange("physical_details.height", {
        value: "",
        unit: heightUnit
      });
      setHeightError("");
      return false;
    }

    if (heightUnit === "feet") {
      // Try to parse feet'inches" format
      const feetInchesPattern = /^(\d{1,2})'(\d{1,2})"?$/;
      const feetInchesMatch = value.match(feetInchesPattern);

      if (feetInchesMatch) {
        const feet = parseInt(feetInchesMatch[1]);
        const inches = parseInt(feetInchesMatch[2]);

        if (feet < 3 || feet > 9) {
          setHeightError("Feet should be between 3 and 9");
          return false;
        }

        if (inches < 0 || inches > 11) {
          setHeightError("Inches should be between 0 and 11");
          return false;
        }

        if (feet === 9 && inches > 0) {
          setHeightError("Maximum height is 9'0\"");
          return false;
        }

        const finalValue = `${feet}'${inches}"`;
        handleInputChange("physical_details.height", {
          value: finalValue,
          unit: "feet"
        });
        setHeightDisplayValue(finalValue);
        setHeightError("");
        return true;
      }

      // Try to parse decimal format like 5.8, 5.10
      const decimalPattern = /^(\d{1,2})\.(\d{1,2})$/;
      const decimalMatch = value.match(decimalPattern);

      if (decimalMatch) {
        const feet = parseInt(decimalMatch[1]);
        const decimalPart = decimalMatch[2];

        // Convert decimal part to inches
        let inches;
        if (decimalPart.length === 2 && decimalPart.startsWith('1')) {
          inches = parseInt(decimalPart);
        } else if (decimalPart.length === 2) {
          inches = parseInt(decimalPart);
        } else if (decimalPart.length === 1) {
          inches = parseInt(decimalPart);
        } else {
          setHeightError("Invalid format. Use feet.inches (e.g., 5.8, 5.10)");
          return false;
        }

        if (isNaN(inches) || inches < 0 || inches > 11) {
          setHeightError("Inches should be between 0 and 11");
          return false;
        }

        if (feet < 3 || feet > 9) {
          setHeightError("Feet should be between 3 and 9");
          return false;
        }

        if (feet === 9 && inches > 0) {
          setHeightError("Maximum height is 9'0\"");
          return false;
        }

        const finalValue = `${feet}'${inches}"`;
        handleInputChange("physical_details.height", {
          value: finalValue,
          unit: "feet"
        });
        setHeightDisplayValue(finalValue);
        setHeightError("");
        return true;
      }

      // Try to parse simple number (just feet)
      const justFeetPattern = /^(\d{1,2})$/;
      const justFeetMatch = value.match(justFeetPattern);

      if (justFeetMatch) {
        const feet = parseInt(justFeetMatch[1]);

        if (feet < 3 || feet > 9) {
          setHeightError("Feet should be between 3 and 9");
          return false;
        }

        const finalValue = `${feet}'0"`;
        handleInputChange("physical_details.height", {
          value: finalValue,
          unit: "feet"
        });
        setHeightDisplayValue(finalValue);
        setHeightError("");
        return true;
      }

      setHeightError("Use format: feet'inches\" (e.g., 5'8\") or feet.inches (e.g., 5.8, 5.10)");
      return false;
    } else {
      // Centimeter mode - exactly like PreferencesStep
      const cm = parseInt(value);
      if (isNaN(cm)) {
        setHeightError("Please enter a valid number");
        return false;
      }

      if (cm < 100 || cm > 250) {
        setHeightError("Height should be between 100cm and 250cm");
        return false;
      }

      handleInputChange("physical_details.height", {
        value: value,
        unit: "cm"
      });
      setHeightError("");
      return true;
    }
  };

  // Languages handlers
  const handleLanguagesChange = (e) => {
    const value = e.target.value;
    setLanguagesText(value);
  };

  const handleLanguagesBlur = () => {
    if (languagesText.trim() === "") {
      handleInputChange("personal_details.languages_known", []);
    } else {
      const languagesArray = languagesText
        .split(',')
        .map(lang => lang.trim())
        .filter(lang => lang.length > 0);

      handleInputChange("personal_details.languages_known", languagesArray);
      setLanguagesText(languagesArray.join(", "));
    }
  };

  // Body type handlers
  const handleBodyTypeInputChange = (e) => {
    const value = e.target.value;
    setBodyTypeInput(value);
    setShowBodyTypeOptions(true);
    handleInputChange("physical_details.body_type", value);
  };

  const handleBodyTypeSelect = (value) => {
    setBodyTypeInput(value);
    handleInputChange("physical_details.body_type", value);
    setShowBodyTypeOptions(false);
  };

  // Complexion handlers
  const handleComplexionInputChange = (e) => {
    const value = e.target.value;
    setComplexionInput(value);
    setShowComplexionOptions(true);
    handleInputChange("physical_details.complexion", value);
  };

  const handleComplexionSelect = (value) => {
    setComplexionInput(value);
    handleInputChange("physical_details.complexion", value);
    setShowComplexionOptions(false);
  };

  // Residential status handlers
  const handleResidentialInputChange = (e) => {
    const value = e.target.value;
    setResidentialInput(value);
    setShowResidentialOptions(true);
    handleInputChange("residence.residential_status", value);
  };

  const handleResidentialSelect = (value) => {
    setResidentialInput(value);
    handleInputChange("residence.residential_status", value);
    setShowResidentialOptions(false);
  };

  // Filter options
  const filteredBodyTypeOptions = options.body_types.filter(option =>
    option.toLowerCase().includes(bodyTypeInput.toLowerCase())
  );

  const filteredComplexionOptions = options.complexions.filter(option =>
    option.toLowerCase().includes(complexionInput.toLowerCase())
  );

  const filteredResidentialOptions = options.residential_statuses.filter(option =>
    option.toLowerCase().includes(residentialInput.toLowerCase())
  );

  return (
    <Grid container spacing={3}>
      {/* Personal Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary">
          Personal Details
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Full Name"
          value={formData.personal_details?.full_name || ""}
          onChange={(e) => handleInputChange("personal_details.full_name", e.target.value)}
          required={true}
          error={!!errors.full_name}
          helperText={errors.full_name}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Date of Birth"
          value={formData.personal_details?.date_of_birth || ""}
          onChange={handleDateChange}
          required={true}
          error={!!dateError || !!errors.date_of_birth}
          helperText={dateError || errors.date_of_birth}
          inputProps={{
            maxLength: 10,
            placeholder: "DD-MM-YYYY"
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Nationality"
          value={formData.personal_details?.nationality || ""}
          onChange={(e) => handleInputChange("personal_details.nationality", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Place of Birth"
          value={formData.personal_details?.place_of_birth || ""}
          onChange={(e) => handleInputChange("personal_details.place_of_birth", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Mother Tongue"
          value={formData.personal_details?.mother_tongue || ""}
          onChange={(e) => handleInputChange("personal_details.mother_tongue", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Languages Known"
          value={languagesText}
          onChange={handleLanguagesChange}
          onBlur={handleLanguagesBlur}
          placeholder="e.g., Urdu, English, Punjabi"
          fullWidth
        />
      </Grid>

      {/* Mobile Number with Country Code */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Mobile Number"
          value={mobileDisplay}
          onChange={handleMobileChange}
          required={true}
          error={!!errors.mobile_number}
          helperText={errors.mobile_number || ""}
          placeholder="Enter mobile number"
          inputProps={{
            type: "tel",
            inputMode: "numeric",
            maxLength: 15
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isMobileCodeEditing ? (
                  <TextField
                    value={mobileCodeInput}
                    onChange={handleMobileCodeInputChange}
                    onBlur={handleMobileCodeBlur}
                    size="small"
                    autoFocus
                    sx={{
                      width: '90px',
                      '& .MuiInputBase-input': {
                        padding: '4px 8px',
                        fontSize: '0.875rem'
                      },
                      '& fieldset': { border: 'none' }
                    }}
                    inputProps={{
                      style: { textAlign: 'center' }
                    }}
                  />
                ) : (
                  <Select
                    value={mobileCountryCode}
                    onChange={handleMobileCountryCodeSelect}
                    onOpen={() => setIsMobileCodeEditing(false)}
                    size="small"
                    sx={{
                      '& .MuiSelect-select': {
                        padding: '4px 8px',
                        fontSize: '0.875rem',
                        minWidth: '70px',
                        cursor: 'pointer'
                      },
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={`${country.value}-${country.country}`} value={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </InputAdornment>
            ),
            onClick: (e) => {
              // Double-click to edit
              if (e.detail === 2) {
                setIsMobileCodeEditing(true);
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Occupation"
          value={formData.personal_details?.occupation || ""}
          onChange={(e) => handleInputChange("personal_details.occupation", e.target.value)}
        />
      </Grid>

      {/* Physical Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
          Physical Details
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Height Field - Exactly like PreferencesStep */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Height"
          value={heightDisplayValue}
          onChange={handleHeightChange}
          onBlur={validateHeight}
          error={!!heightError}
          helperText={heightError}
          placeholder={heightUnit === "feet" ? "5.8 or 5'08\"" : "180"}
          inputProps={{
            maxLength: heightUnit === "feet" ? 6 : 3
          }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Select
                  value={heightUnit}
                  onChange={handleHeightUnitChange}
                  size="small"
                  sx={{
                    '& .MuiSelect-select': {
                      padding: '4px 8px',
                      fontSize: '0.875rem',
                      minWidth: '60px'
                    },
                    '& fieldset': { border: 'none' }
                  }}
                >
                  <MenuItem value="feet">Feet</MenuItem>
                  <MenuItem value="cm">cm</MenuItem>
                </Select>
              </InputAdornment>
            )
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Weight (kg)"
          value={formData.physical_details?.weight_kg || ""}
          onChange={(e) => handleInputChange("physical_details.weight_kg", e.target.value)}
          type="number"
          inputProps={{ min: 30, max: 200 }}
        />
      </Grid>

      {/* Body Type Field */}
      <Grid item xs={12} sm={6}>
        <div style={{ position: 'relative' }}>
          <TextInput
            label="Body Type"
            value={bodyTypeInput}
            onChange={handleBodyTypeInputChange}
            onFocus={() => setShowBodyTypeOptions(true)}
            onBlur={() => setTimeout(() => setShowBodyTypeOptions(false), 200)}
            placeholder="Type or select from options"
            fullWidth
          />
          {showBodyTypeOptions && filteredBodyTypeOptions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {filteredBodyTypeOptions.map((option) => (
                <div
                  key={option}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee'
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleBodyTypeSelect(option);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </Grid>

      {/* Complexion Field */}
      <Grid item xs={12} sm={6}>
        <div style={{ position: 'relative' }}>
          <TextInput
            label="Complexion"
            value={complexionInput}
            onChange={handleComplexionInputChange}
            onFocus={() => setShowComplexionOptions(true)}
            onBlur={() => setTimeout(() => setShowComplexionOptions(false), 200)}
            placeholder="Type or select from options"
            fullWidth
          />
          {showComplexionOptions && filteredComplexionOptions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {filteredComplexionOptions.map((option) => (
                <div
                  key={option}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee'
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleComplexionSelect(option);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </Grid>

      {/* Residence Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
          Residence
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Residential Status Field */}
      <Grid item xs={12} sm={6}>
        <div style={{ position: 'relative' }}>
          <TextInput
            label="Residential Status"
            value={residentialInput}
            onChange={handleResidentialInputChange}
            onFocus={() => setShowResidentialOptions(true)}
            onBlur={() => setTimeout(() => setShowResidentialOptions(false), 200)}
            placeholder="Type or select from options"
            fullWidth
          />
          {showResidentialOptions && filteredResidentialOptions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {filteredResidentialOptions.map((option) => (
                <div
                  key={option}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee'
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleResidentialSelect(option);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Country"
          value={formData.residence?.country || ""}
          onChange={(e) => handleInputChange("residence.country", e.target.value)}
          required={true}
          error={!!errors.country}
          helperText={errors.country}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="State / Province"
          value={formData.residence?.state || ""}
          onChange={(e) => handleInputChange("residence.state", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="City"
          value={formData.residence?.city || ""}
          onChange={(e) => handleInputChange("residence.city", e.target.value)}
          required={true}
          error={!!errors.city}
          helperText={errors.city}
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextInput
          label="Permanent Address"
          value={formData.personal_details?.permanent_address || ""}
          onChange={(e) => handleInputChange("personal_details.permanent_address", e.target.value)}
          multiline
          rows={2}
        />
      </Grid>
    </Grid>
  );
};

export default PersonalDetailsStep;